/**
 * Excel Hover Data Lookup
 * Contains the large Excel specification lookup table
 * Extracted from milestoneConfigEnhanced.ts for better modularity
 */

/**
 * Get Excel hover data for a specific combination of approval condition, review type, and status
 * This implements the Excel specification lookup table
 */
export const getExcelHoverData = (
  approvalCondition: string,
  reviewType: string,
  statusMenu: string
): {
  hoverText: string;
  editText: string;
  rolloverCopy?: string;
} | null => {
  const combinations: {
    [key: string]: {
      hoverText: string;
      editText: string;
      rolloverCopy?: string;
    };
  } = {
    APPROVAL_REQUIRED_MEETING_TARGET: {
      hoverText: 'Meeting Target: [Mon] or [Mon XX]',
      editText: ''
    },
    APPROVAL_REQUIRED_MEETING_SCHEDULED: {
      hoverText: 'Meeting Scheduled on: [Mon XX] at [11:11AM - 12:12PM PST]',
      editText: ''
    },
    APPROVAL_REQUIRED_MEETING_NOT_APPROVED: {
      hoverText: 'Not Approved on: [Mon XX]',
      editText: ''
    },
    APPROVAL_REQUIRED_MEETING_NOT_APPROVED_TO_BE_APPROVED_ASYNC: {
      hoverText: 'Not Approved on: [Mon XX]',
      editText: 'To Be Approved Async. Target: [Mon XX]'
    },
    APPROVAL_REQUIRED_MEETING_REPLANNING_APPROVAL_REQUIRED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    'APPROVAL_REQUIRED_MEETING_NOT_APPROVED_-_TO_BE_APPROVED_ASYNC': {
      hoverText: 'Not Approved on: [Mon XX]',
      editText: 'To Be Approved Async. Target: [Mon XX]'
    },
    APPROVAL_REQUIRED_MEETING_APPROVED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    'APPROVAL_REQUIRED_MEETING_APPROVED_-_NEEDS_ASYNC_FOLLOW-UP': {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Needs Async Follow-Up. Target: [Mon XX]'
    },
    // Add mapping for APPROVED_FOLLOW_UP status key
    APPROVAL_REQUIRED_MEETING_APPROVED_FOLLOW_UP: {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Needs Async Follow-Up. Target: [Mon XX]'
    },

    // Add mapping for RE_APPROVED status key (in case it comes without hyphen)
    APPROVAL_REQUIRED_MEETING_RE_APPROVED: {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    APPROVAL_REQUIRED_ASYNC_TARGET: {
      hoverText: 'Send Target: [Mon] or [Mon XX]',
      editText: ''
    },
    // Async → Sent (Approval Required)
    // Match Figma: "Sent on: Sep 30, 2025. Leadership Feedback Due by: Oct 7, 2025"
    APPROVAL_REQUIRED_ASYNC_SENT: {
      hoverText: 'Sent on: [Mon XX, YYYY]',
      editText: 'Leadership Feedback Due by: [Mon XX, YYYY]'
    },
    APPROVAL_REQUIRED_ASYNC_SENT_FEEDBACK_PENDING: {
      hoverText: 'Sent on: [Mon XX, YYYY]',
      editText: 'Pending Team Response. Target: [Mon XX, YYYY]'
    },
    APPROVAL_REQUIRED_ASYNC_NOT_APPROVED: {
      hoverText: 'Not Approved on: [Mon XX]',
      editText: ''
    },
    APPROVAL_REQUIRED_ASYNC_APPROVED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    APPROVAL_REQUIRED_ASYNC_REPLANNING_APPROVAL_REQUIRED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    'APPROVAL_REQUIRED_ASYNC_RE-APPROVED': {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    // Add mapping for RE_APPROVED_ASYNC status key (without hyphen)
    APPROVAL_REQUIRED_ASYNC_RE_APPROVED: {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    NO_APPROVAL_REQUIRED_MEETING_TARGET: {
      hoverText: 'Meeting Target: [Mon] or [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_SCHEDULED: {
      hoverText: 'Meeting Scheduled on: [Mon XX] at [11:11AM - 12:12PM UTC]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_NOT_APPROVED: {
      hoverText: 'Not Approved on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_ASYNC_TARGET: {
      hoverText: 'Send Target: [Mon] or [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_ASYNC_SENT: {
      hoverText: 'Sent on: [Mon XX, YYYY]',
      editText: 'Team Feedback Due by: [Mon XX, YYYY]'
    },
    NO_APPROVAL_REQUIRED_ASYNC_SENT_FEEDBACK_PENDING: {
      hoverText: 'Sent on: [Mon XX, YYYY]',
      editText: 'Pending Team Response. Target: [Mon XX, YYYY]'
    },
    NO_APPROVAL_REQUIRED_ASYNC_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_REPLANNING_APPROVAL_REQUIRED: {
      hoverText: 'Originally Completed on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_ASYNC_REPLANNING_APPROVAL_REQUIRED: {
      hoverText: 'Originally Completed on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_COMPLETED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_ASYNC_COMPLETED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_MEETING_APPROVED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_ASYNC_APPROVED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: ''
    },
    'NO_APPROVAL_REQUIRED_MEETING_RE-APPROVED': {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    'NO_APPROVAL_REQUIRED_ASYNC_RE-APPROVED': {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    // Add mappings without hyphens
    NO_APPROVAL_REQUIRED_MEETING_RE_APPROVED: {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    NO_APPROVAL_REQUIRED_ASYNC_RE_APPROVED: {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    // Add FOLLOW UP mappings
    'NO_APPROVAL_REQUIRED_MEETING_APPROVED_-_NEEDS_ASYNC_FOLLOW-UP': {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Needs Async Follow-Up. Target: [Mon XX]'
    },
    NO_APPROVAL_REQUIRED_MEETING_APPROVED_FOLLOW_UP: {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Needs Async Follow-Up. Target: [Mon XX]'
    },

    // TBD combinations
    TBD_MEETING_TBD: {
      hoverText: 'TBD',
      editText: ''
    },
    TBD_ASYNC_TBD: {
      hoverText: 'TBD',
      editText: ''
    },
    TBD_NOT_REQUIRED_TBD: {
      hoverText: 'TBD',
      editText: ''
    },

    // NOT REQUIRED combinations
    NOT_REQUIRED_NOT_REQUIRED_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    NOT_REQUIRED_MEETING_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    NOT_REQUIRED_ASYNC_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    NO_APPROVAL_REQUIRED_NOT_REQUIRED_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    APPROVAL_REQUIRED_NOT_REQUIRED_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    APPROVAL_REQUIRED_MEETING_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },
    APPROVAL_REQUIRED_ASYNC_NOT_REQUIRED: {
      hoverText: 'Milestone Not Required',
      editText: ''
    },

    // 'Re-Planning / Approval Required' Combinations
    APPROVAL_REQUIRED_MEETING_REPLANNING_OR_APPROVAL_REQUIRED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Re-Planning or Approval Required'
    },
    APPROVAL_REQUIRED_ASYNC_REPLANNING_OR_APPROVAL_REQUIRED: {
      hoverText: 'Approved on: [Mon XX]',
      editText: 'Re-Planning or Approval Required'
    },
    NO_APPROVAL_REQUIRED_MEETING_REPLANNING_OR_APPROVAL_REQUIRED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: 'Re-Planning or Approval Required'
    },
    NO_APPROVAL_REQUIRED_ASYNC_REPLANNING_OR_APPROVAL_REQUIRED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: 'Re-Planning or Approval Required'
    },

    // Completed combinations
    APPROVAL_REQUIRED_MEETING_COMPLETED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: ''
    },
    APPROVAL_REQUIRED_ASYNC_COMPLETED: {
      hoverText: 'Completed on: [Mon XX]',
      editText: ''
    },

    // Alternative key formats for flexibility
    'APPROVAL_REQUIRED_MEETING_RE-APPROVED': {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    },
    'APPROVAL_REQUIRED_ASYNC_RE-APPROVED_2': {
      hoverText: 'Originally Approved on: [Mon XX]',
      editText: 'Re-Approved on: [Mon XX]'
    }
  };

  // Create lookup key
  const key = `${approvalCondition}_${reviewType}_${statusMenu}`;

  // Return the matched combination or null
  return combinations[key] || null;
};
